/**
 * Command palette — universal ⌘K finder + verb-launcher.
 *
 * Per Decisions §19 (and INTERACTION_SPEC §10): a single primitive
 * that surfaces tickets, contracts/orgs, people, app commands, and
 * navigation entries. Typing filters across all of them; empty input
 * shows recent tickets + suggested commands.
 *
 * Result groups (in priority order):
 *   1. Tickets       → match T-####, subject, contract name
 *   2. Contracts     → match name + org name
 *   3. People        → assignable staff users
 *   4. Commands      → verbs (Toggle theme, Sign out, …)
 *   5. Navigation    → Go to Inbox, Reports, …
 *
 * Built on `cmdk` — handles fuzzy search, keyboard nav (↑/↓/↵), and
 * focus management. We slot Beacon's visual primitives (icons,
 * mono numbering) inside.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import {
  Building2,
  Clock,
  FileText,
  Inbox,
  Keyboard,
  LogOut,
  Moon,
  Plus,
  Search,
  Sun,
  User as UserIcon,
  Users as UsersIcon,
  Zap,
} from 'lucide-react';

import { useContracts } from '@/hooks/use-contracts.js';
import { useOrganizations } from '@/hooks/use-organizations.js';
import { useRecentTickets } from '@/hooks/use-recent-tickets.js';
import { useTickets } from '@/hooks/use-tickets.js';
import { useAssignableUsers } from '@/hooks/use-users.js';
import { useViewer } from '@/hooks/use-viewer.js';
import { useHotkeys } from '@/hooks/use-hotkeys.js';
import { contractDisplay } from '@/lib/contract-display.js';
import { ContractLogo } from '@/components/beacon/contract-logo.js';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onOpenCheatSheet: () => void;
  onToggleTheme: () => void;
}

export function CommandPalette({
  open,
  onClose,
  onOpenCheatSheet,
  onToggleTheme,
}: CommandPaletteProps) {
  const navigate = useNavigate();
  const viewer = useViewer().data;
  const isClient = viewer?.organizationType === 'client';
  const tickets = useTickets().data ?? [];
  const contracts = useContracts().data ?? [];
  const orgs = useOrganizations().data ?? [];
  // Staff-only API; client viewers get a 403 if we fire it.
  const users = useAssignableUsers({ enabled: !isClient }).data ?? [];
  const recents = useRecentTickets();
  const [search, setSearch] = useState('');

  // Reset query whenever the palette closes — opening it again should
  // start fresh, like Spotlight.
  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  // Esc closes — belt-and-braces beside cmdk's own handling.
  useHotkeys({ escape: () => onClose() }, { disabled: !open, inInput: true });

  // Slim, indexed view of the data so cmdk's filter has cheap strings.
  const orgById = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of orgs) m.set(o.id, o.name);
    return m;
  }, [orgs]);

  const visibleTickets = useMemo(
    () => tickets.slice(0, 200), // Cap to keep first paint snappy
    [tickets]
  );

  function go(path: string) {
    onClose();
    navigate(path);
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh]">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-[4px] transition-opacity duration-200"
        onClick={onClose}
        aria-hidden="true"
      />
      <Command
        label="Command palette"
        className="relative w-[640px] max-w-[92vw] bg-ink-850 border border-ink-600 rounded-lg shadow-2xl overflow-hidden flex flex-col"
        loop
      >
        <div className="flex items-center px-3 h-12 border-b border-ink-600 gap-2">
          <Search size={15} className="text-ink-300 shrink-0" aria-hidden="true" />
          <Command.Input
            value={search}
            onValueChange={setSearch}
            placeholder="Search tickets, contracts, people, commands…"
            className="flex-1 bg-transparent text-[14px] text-ink-100 placeholder:text-ink-400 focus:outline-none"
          />
          <kbd className="text-[10.5px] text-ink-400 mono px-1.5 py-0.5 rounded bg-ink-800 border border-ink-600">
            Esc
          </kbd>
        </div>

        <Command.List className="max-h-[60vh] overflow-auto py-1">
          <Command.Empty className="px-4 py-8 text-center text-[12.5px] text-ink-300">
            No matches.
          </Command.Empty>

          {/* Recent — only when input is empty */}
          {!search.trim() && recents.length > 0 && (
            <Group heading="Recent">
              {recents.slice(0, 5).map((r) => (
                <Item
                  key={`recent-${r.number}`}
                  value={`recent ${r.number} ${r.subject} ${r.contractName}`}
                  onSelect={() => go(`/tickets/${r.number}`)}
                  icon={<Clock size={13} />}
                  primary={r.subject}
                  meta={r.number}
                  secondary={r.contractName}
                />
              ))}
            </Group>
          )}

          {/* Tickets */}
          <Group heading="Tickets">
            {visibleTickets.map((t) => {
              const c = contracts.find((x) => x.id === t.contractId);
              const display = c
                ? contractDisplay(c)
                : { tag: '??', color: '#4A4F5A' };
              return (
                <Item
                  key={`tkt-${t.id}`}
                  value={`ticket ${t.number} ${t.subject} ${c?.name ?? ''}`}
                  onSelect={() => go(`/tickets/${t.number}`)}
                  icon={<ContractLogo contract={display} size={16} />}
                  primary={t.subject}
                  meta={t.number}
                  secondary={c?.name}
                />
              );
            })}
          </Group>

          {/* Contracts (staff only) */}
          {!isClient && contracts.length > 0 && (
            <Group heading="Contracts">
              {contracts.map((c) => (
                <Item
                  key={`con-${c.id}`}
                  value={`contract ${c.name} ${orgById.get(c.organizationId) ?? ''}`}
                  onSelect={() =>
                    go(`/admin/clients/${c.organizationId}/contracts/${c.id}`)
                  }
                  icon={<Building2 size={13} />}
                  primary={c.name}
                  secondary={orgById.get(c.organizationId)}
                />
              ))}
            </Group>
          )}

          {/* Organizations (staff only) */}
          {!isClient && orgs.length > 0 && (
            <Group heading="Organizations">
              {orgs.map((o) => (
                <Item
                  key={`org-${o.id}`}
                  value={`org ${o.name}`}
                  onSelect={() => go(`/admin/clients/${o.id}`)}
                  icon={<Building2 size={13} />}
                  primary={o.name}
                  secondary={o.type}
                />
              ))}
            </Group>
          )}

          {/* People (staff only) */}
          {!isClient && users.length > 0 && (
            <Group heading="People">
              {users.map((u) => (
                <Item
                  key={`usr-${u.id}`}
                  value={`person ${u.name} ${u.email}`}
                  onSelect={() => go('/admin/users')}
                  icon={<UserIcon size={13} />}
                  primary={u.name}
                  secondary={u.email}
                />
              ))}
            </Group>
          )}

          {/* Commands */}
          <Group heading="Commands">
            <Item
              value="command toggle theme"
              onSelect={() => {
                onToggleTheme();
                onClose();
              }}
              icon={<Moon size={13} />}
              primary="Toggle theme"
            />
            <Item
              value="command keyboard shortcuts cheat sheet"
              onSelect={() => {
                onClose();
                onOpenCheatSheet();
              }}
              icon={<Keyboard size={13} />}
              primary="Keyboard shortcuts"
              meta="⌘ ?"
            />
            {!isClient && (
              <Item
                value="command new ticket compose"
                onSelect={() => go('/inbox?compose=1')}
                icon={<Plus size={13} />}
                primary="New ticket"
              />
            )}
            <Item
              value="command sign out logout"
              onSelect={() => go('/login')}
              icon={<LogOut size={13} />}
              primary="Sign out"
            />
          </Group>

          {/* Navigation */}
          <Group heading="Navigation">
            {isClient ? (
              <>
                <Item
                  value="navigation go to home"
                  onSelect={() => go('/home')}
                  icon={<Sun size={13} />}
                  primary="Go to Home"
                />
                <Item
                  value="navigation go to my tickets"
                  onSelect={() => go('/tickets')}
                  icon={<Inbox size={13} />}
                  primary="Go to My tickets"
                />
              </>
            ) : (
              <>
                <Item
                  value="navigation go to inbox"
                  onSelect={() => go('/inbox')}
                  icon={<Inbox size={13} />}
                  primary="Go to Inbox"
                />
                <Item
                  value="navigation go to internal board"
                  onSelect={() => go('/internal')}
                  icon={<FileText size={13} />}
                  primary="Go to Internal board"
                />
                <Item
                  value="navigation go to reports"
                  onSelect={() => go('/reports')}
                  icon={<FileText size={13} />}
                  primary="Go to Reports"
                />
                <Item
                  value="navigation go to clients admin"
                  onSelect={() => go('/admin/clients')}
                  icon={<Building2 size={13} />}
                  primary="Go to Clients"
                />
                <Item
                  value="navigation go to users admin"
                  onSelect={() => go('/admin/users')}
                  icon={<UsersIcon size={13} />}
                  primary="Go to Users"
                />
                <Item
                  value="navigation go to automations admin"
                  onSelect={() => go('/admin/automations')}
                  icon={<Zap size={13} />}
                  primary="Go to Automations"
                />
              </>
            )}
          </Group>
        </Command.List>

        <div className="px-3 h-9 border-t border-ink-600 flex items-center justify-between text-[10.5px] text-ink-400 mono">
          <span>
            <kbd>↑↓</kbd> to navigate · <kbd>↵</kbd> to open
          </span>
          <span>
            <kbd>Esc</kbd> to close
          </span>
        </div>
      </Command>
    </div>
  );
}

function Group({
  heading,
  children,
}: {
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <Command.Group
      heading={heading}
      className="px-1 py-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[10.5px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.12em] [&_[cmdk-group-heading]]:text-ink-400 [&_[cmdk-group-heading]]:font-semibold"
    >
      {children}
    </Command.Group>
  );
}

interface ItemProps {
  value: string;
  onSelect: () => void;
  icon: React.ReactNode;
  /** Primary line — usually the entity name. */
  primary: React.ReactNode;
  /** Secondary line — sub-context, smaller + dimmer. */
  secondary?: React.ReactNode;
  /** Right-aligned meta — ticket number, kbd shortcut, etc. */
  meta?: React.ReactNode;
}

function Item({ value, onSelect, icon, primary, secondary, meta }: ItemProps) {
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      className="flex items-center gap-2.5 px-2.5 h-9 rounded-md cursor-pointer text-[13px] text-ink-100 data-[selected=true]:bg-ink-700 data-[selected=true]:text-ink-100 transition-colors"
    >
      <span className="text-ink-300 shrink-0 w-4 h-4 inline-flex items-center justify-center">
        {icon}
      </span>
      <span className="flex-1 min-w-0 truncate">{primary}</span>
      {secondary && (
        <span className="text-[12px] text-ink-300 truncate max-w-[180px]">
          {secondary}
        </span>
      )}
      {meta && (
        <span className="text-[11px] text-ink-400 mono shrink-0">{meta}</span>
      )}
    </Command.Item>
  );
}
